import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { MDBTable, MDBBtn } from "mdbreact";
import Swal from "sweetalert2";

import {
  DESTROY,
  SetEDIT,
} from "../../../../../services/redux/slices/diagnostics/clinic/clinicMenus";
import {
  getMenuPhysicians,
  getPhysicianName,
  getPhysicianRecordId,
} from "./utils";

const buildDosage = (row = {}) => {
  if (row?.dosage) return row.dosage;
  if (row?.itemType !== "meds") return "-";
  if (!row?.strength) return row?.unit || "-";

  return `${row.strength} ${row.measure || ""}/${row.unit || "pcs"}`
    .replace(/\s+/g, " ")
    .trim();
};

export default function Body() {
  const dispatch = useDispatch();
  const {
    filtered = [],
    activePage,
    maxPage,
    activeView,
  } = useSelector(({ clinicMenus }) => clinicMenus);
  const { token, activePlatform, auth } = useSelector(({ auth }) => auth);
  const { collections: clinics = [] } = useSelector(
    ({ clinicInfo }) => clinicInfo,
  );

  const physicians = getMenuPhysicians({
    activePlatform,
    clinics,
    authId: auth?._id,
  }).map((physician) => ({
    ...physician,
    recordId: getPhysicianRecordId(physician),
    recordName: getPhysicianName(physician),
  }));

  const viewFiltered = filtered.filter(({ itemType }) =>
    activeView === "meds" ? itemType === "meds" : itemType !== "meds",
  );
  const sortedData = React.useMemo(
    () =>
      [...viewFiltered].sort((a, b) =>
        String(a?.abbreviation || "").localeCompare(
          String(b?.abbreviation || ""),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [viewFiltered],
  );
  const startIndex = (activePage - 1) * maxPage;
  const endIndex = startIndex + maxPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleDelete = (_id, abbreviation) => {
    Swal.fire({
      title: "Are you sure?",
      text: `${abbreviation} permanently delete the clinic menu.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DESTROY({ token, data: { id: _id } }));
      }
    });
  };

  return (
    <MDBTable responsive hover bordered>
      <thead>
        <tr>
          <th>#</th>
          <th>Service/Product</th>
          <th>Dosages</th>
          <th>SRP</th>
          <th>Doctor/Specialist</th>
          <th>Discountable</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {paginatedData.length > 0 ? (
          paginatedData.map((row, index) => {
            if (!row) return null;

            const {
              abbreviation,
              description,
              srp,
              discountable,
              physician,
              _id,
            } = row;
            const dosage = buildDosage(row);

            const doctor = physicians.find(
              (doc) =>
                String(doc?.recordId) ===
                String(physician?._id || physician || ""),
            );

            return (
              <tr key={_id || index}>
                <td>{startIndex + index + 1}</td>
                <td>
                  {abbreviation || "-"}
                  <div className="small text-muted">{description}</div>
                </td>
                <td>{dosage}</td>
                <td>
                  {typeof srp === "number" ? `P ${srp.toLocaleString()}` : "-"}
                </td>
                <td>{doctor?.recordName ? `Dr. ${doctor.recordName}` : "-"}</td>
                <td>{discountable ? "Yes" : "No"}</td>
                <td>
                  <div className="d-flex" style={{ gap: "8px" }}>
                    <MDBBtn
                      size="sm"
                      color="info"
                      onClick={() => dispatch(SetEDIT(row))}
                    >
                      Edit
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="danger"
                      onClick={() => handleDelete(_id, abbreviation)}
                    >
                      Delete
                    </MDBBtn>
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="7" className="text-center">
              No {activeView === "meds" ? "meds" : "services"} found. Click{" "}
              <strong>Add</strong> to create one.
            </td>
          </tr>
        )}
      </tbody>
    </MDBTable>
  );
}
